import { preisToolApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  ApplyFixedPriceImportRequest,
  CreateManualOfferRequest,
  PreviewFixedPriceImportRequest,
  FixedPriceImportPreview,
  FixedPriceImportResult,
  PriceListColumnNamesResponse,
  SavePriceListColumnNamesRequest,
  ListPreisToolItemsFilters,
  ListSupplierAnomaliesFilters,
  PreisToolDisposition,
  PreisToolDispositionSettings,
  PreisToolDispositionSettingsValues,
  PreisToolFacetsResponse,
  PreisToolFixedPricesResponse,
  PreisToolItemDetailResponse,
  PreisToolItemsResponse,
  PreisToolLegendThresholds,
  PreisToolManualOffer,
  PreisToolPriceChangeBatchResponse,
  PreisToolSupplierTerms,
  PreisToolPriceHistoryResponse,
  PreisToolPriceWritebackStatusResponse,
  PreisToolSalesHistoryResponse,
  PreisToolSupplierAnomaliesResponse,
  PreisToolSupplierOffersResponse,
  PreisToolSuppliersResponse,
  PreisToolUpstreamSupplierNamesResponse,
  SaveFixedPriceRequest,
  SaveUpstreamSupplierRequest,
  SavePreisToolLegendThresholdsRequest,
  SavePreisToolPricesRequest,
  SavePreisToolPricesResponse,
} from './preis-tool.types';

/** How often a transfer that is on its way to SAP is checked for its outcome. */
/** How often a save is checked while its prices are on their way to SAP. */
const TRANSFER_POLL_MS = 3_000;

export const usePreisToolItems = (filters?: ListPreisToolItemsFilters) =>
  useQuery<PreisToolItemsResponse>({
    queryKey: queryKeys.preisTool.items(filters),
    queryFn: () => preisToolApi.list(filters),
    placeholderData: keepPreviousData,
  });

export const usePreisToolFacets = () =>
  useQuery<PreisToolFacetsResponse>({
    queryKey: queryKeys.preisTool.facets(),
    queryFn: () => preisToolApi.facets(),
    staleTime: 5 * 60 * 1000,
  });

export const usePreisToolItemDetail = (itemId: string | undefined) =>
  useQuery<PreisToolItemDetailResponse>({
    queryKey: queryKeys.preisTool.itemDetail(itemId!),
    queryFn: () => preisToolApi.itemDetail(itemId!),
    enabled: !!itemId,
  });

export const usePreisToolSupplierOffers = (itemId: string | undefined) =>
  useQuery<PreisToolSupplierOffersResponse>({
    queryKey: queryKeys.preisTool.supplierOffers(itemId!),
    queryFn: () => preisToolApi.supplierOffers(itemId!),
    enabled: !!itemId,
  });

export const usePreisToolSupplierAnomalies = (
  filters?: ListSupplierAnomaliesFilters,
) =>
  useQuery<PreisToolSupplierAnomaliesResponse>({
    queryKey: queryKeys.preisTool.supplierAnomalies(filters),
    queryFn: () => preisToolApi.supplierAnomalies(filters),
    staleTime: 60 * 1000,
  });

/** Limits of the colour legend (EOS-61), shared by every user. */
export const usePreisToolLegendThresholds = () =>
  useQuery<PreisToolLegendThresholds>({
    queryKey: queryKeys.preisTool.legendThresholds(),
    queryFn: () => preisToolApi.legendThresholds(),
    staleTime: 60 * 1000,
  });

/**
 * Saves the legend limits for everybody. The anomaly feed follows the "Deal"
 * limit, so it is fetched again; row colours and counts are computed in the
 * browser from the limits and update with them.
 */
export const useSavePreisToolLegendThresholds = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolLegendThresholds,
    AxiosError,
    SavePreisToolLegendThresholdsRequest
  >({
    mutationFn: (body) => preisToolApi.saveLegendThresholds(body),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.preisTool.legendThresholds(), saved);
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.legendThresholds(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.supplierAnomaliesAll(),
        }),
      ]);
    },
  });
};

/**
 * Follows saves until SAP answered: each batch is refetched while one of its
 * changes is still `PENDING`.
 */
export const usePreisToolPriceChangeBatches = (batchIds: string[]) =>
  useQueries({
    queries: batchIds.map((batchId) => ({
      queryKey: queryKeys.preisTool.priceChangeBatch(batchId),
      queryFn: () => preisToolApi.priceChangeBatch(batchId),
      refetchInterval: (query: {
        state: { data?: PreisToolPriceChangeBatchResponse };
      }) =>
        query.state.data?.changes.some((c) => c.status === 'PENDING') ===
        false
          ? false
          : TRANSFER_POLL_MS,
    })),
  });

/** After SAP answered a save: everything showing sale prices is refetched. */
export const useRefreshAfterTransfer = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.preisTool.itemsAll(),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.preisTool.priceHistoryAll(),
      }),
    ]);
};

export const usePreisToolPriceHistory = (
  itemId: string | undefined,
  days: number,
) =>
  useQuery<PreisToolPriceHistoryResponse>({
    queryKey: queryKeys.preisTool.priceHistory(itemId!, days),
    queryFn: () => preisToolApi.priceHistory(itemId!, days),
    enabled: !!itemId,
    placeholderData: keepPreviousData,
  });

export const usePreisToolSuppliers = () =>
  useQuery<PreisToolSuppliersResponse>({
    queryKey: queryKeys.preisTool.suppliers(),
    queryFn: () => preisToolApi.suppliers(),
    staleTime: 10 * 60 * 1000,
  });

export const usePreisToolFixedPrices = (itemId: string | undefined) =>
  useQuery<PreisToolFixedPricesResponse>({
    queryKey: queryKeys.preisTool.fixedPrices(itemId!),
    queryFn: () => preisToolApi.fixedPrices(itemId!),
    enabled: !!itemId,
  });

/** Fixed prices are offers too: refresh everything that shows offers. */
const afterFixedPriceChange = (
  queryClient: QueryClient,
  result: PreisToolFixedPricesResponse,
) => {
  queryClient.setQueryData(
    queryKeys.preisTool.fixedPrices(result.itemId),
    result,
  );
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.supplierOffersAll(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.supplierAnomaliesAll(),
    }),
    // A typed-in supplier name appears in or leaves the pick list.
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.suppliers(),
    }),
  ]);
};

export const useSaveFixedPrice = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolFixedPricesResponse,
    AxiosError,
    SaveFixedPriceRequest
  >({
    mutationFn: (body) => preisToolApi.saveFixedPrice(body),
    onSuccess: (result) => afterFixedPriceChange(queryClient, result),
  });
};

export const useRemoveFixedPrice = () => {
  const queryClient = useQueryClient();
  return useMutation<PreisToolFixedPricesResponse, AxiosError, string>({
    mutationFn: (id) => preisToolApi.removeFixedPrice(id),
    onSuccess: (result) => afterFixedPriceChange(queryClient, result),
  });
};

export const usePreisToolUpstreamSupplierNames = () =>
  useQuery<PreisToolUpstreamSupplierNamesResponse>({
    queryKey: queryKeys.preisTool.upstreamSupplierNames(),
    queryFn: () => preisToolApi.upstreamSupplierNames(),
  });

/** The stock of a fixed price shows in the offer table, so that refreshes too. */
const afterUpstreamSupplierChange = (
  queryClient: QueryClient,
  result: PreisToolFixedPricesResponse,
) =>
  Promise.all([
    afterFixedPriceChange(queryClient, result),
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.upstreamSupplierNames(),
    }),
  ]);

export const useSaveUpstreamSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolFixedPricesResponse,
    AxiosError,
    { fixedPriceId: string; body: SaveUpstreamSupplierRequest }
  >({
    mutationFn: ({ fixedPriceId, body }) =>
      preisToolApi.saveUpstreamSupplier(fixedPriceId, body),
    onSuccess: (result) => afterUpstreamSupplierChange(queryClient, result),
  });
};

export const useRemoveUpstreamSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolFixedPricesResponse,
    AxiosError,
    { fixedPriceId: string; upstreamSupplierId: string }
  >({
    mutationFn: ({ fixedPriceId, upstreamSupplierId }) =>
      preisToolApi.removeUpstreamSupplier(fixedPriceId, upstreamSupplierId),
    onSuccess: (result) => afterUpstreamSupplierChange(queryClient, result),
  });
};

/** A manual offer is an offer row: refresh everything that shows offers. */
/** Offers changed: offer tables, anomaly feed and the list's "Bester Lieferant". */
const refreshOffers = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.supplierOffersAll(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.supplierAnomaliesAll(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.itemsAll(),
    }),
    // A typed-in supplier name appears in or leaves the pick list.
    queryClient.invalidateQueries({
      queryKey: queryKeys.preisTool.suppliers(),
    }),
  ]);

export const useSavePreisToolSupplierTerms = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolSupplierTerms,
    AxiosError,
    { supplierBpId: number; shippingCost: number | null }
  >({
    mutationFn: ({ supplierBpId, shippingCost }) =>
      preisToolApi.saveSupplierTerms(supplierBpId, shippingCost),
    onSuccess: () => refreshOffers(queryClient),
  });
};

export const useCreateManualOffer = () => {
  const queryClient = useQueryClient();
  return useMutation<PreisToolManualOffer, AxiosError, CreateManualOfferRequest>({
    mutationFn: (body) => preisToolApi.createManualOffer(body),
    onSuccess: () => refreshOffers(queryClient),
  });
};

export const useRemoveManualOffer = () => {
  const queryClient = useQueryClient();
  return useMutation<{ id: string; itemId: string }, AxiosError, string>({
    mutationFn: (id) => preisToolApi.removeManualOffer(id),
    onSuccess: () => refreshOffers(queryClient),
  });
};

/** Without `targetMargin` the set default target margin applies. */
export const usePreisToolDisposition = (
  itemId: string | undefined,
  targetMargin?: number,
) =>
  useQuery<PreisToolDisposition>({
    queryKey: queryKeys.preisTool.disposition(itemId!, targetMargin ?? null),
    queryFn: () => preisToolApi.disposition(itemId!, targetMargin),
    enabled: !!itemId,
    placeholderData: keepPreviousData,
  });

export const usePreisToolSalesHistory = (
  itemId: string | undefined,
  enabled: boolean,
) =>
  useQuery<PreisToolSalesHistoryResponse>({
    queryKey: queryKeys.preisTool.salesHistory(itemId!),
    queryFn: () => preisToolApi.salesHistory(itemId!),
    enabled: !!itemId && enabled,
  });

export const useSetReorderPoint = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolDisposition,
    AxiosError,
    { itemId: string; reorderPoint: number | null }
  >({
    mutationFn: ({ itemId, reorderPoint }) =>
      preisToolApi.setReorderPoint(itemId, reorderPoint),
    onSuccess: (result) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.preisTool.dispositionOfItem(result.itemId),
      }),
  });
};

// --- Purchasing aid settings (EOS-62) ---

export const usePreisToolDispositionSettings = () =>
  useQuery<PreisToolDispositionSettings>({
    queryKey: queryKeys.preisTool.dispositionSettings(),
    queryFn: () => preisToolApi.dispositionSettings(),
  });

/** Settings changed for everyone: reload them, every purchasing aid and sales history. */
export const useSaveDispositionSettings = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PreisToolDispositionSettings,
    AxiosError,
    PreisToolDispositionSettingsValues
  >({
    mutationFn: (body) => preisToolApi.saveDispositionSettings(body),
    onSuccess: (result) => {
      queryClient.setQueryData(
        queryKeys.preisTool.dispositionSettings(),
        result,
      );
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.dispositionSettings(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.dispositionAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.salesHistoryAll(),
        }),
      ]);
    },
  });
};

// --- end purchasing aid settings ---

export const usePreviewFixedPriceImport = () =>
  useMutation<
    FixedPriceImportPreview,
    AxiosError,
    PreviewFixedPriceImportRequest
  >({
    mutationFn: (request) => preisToolApi.previewFixedPriceImport(request),
  });

/**
 * Imported fixed prices are offers of many items: refresh all of that, and
 * the column names when the import added some.
 */
export const useApplyFixedPriceImport = () => {
  const queryClient = useQueryClient();
  return useMutation<
    FixedPriceImportResult,
    AxiosError,
    ApplyFixedPriceImportRequest
  >({
    mutationFn: (request) => preisToolApi.applyFixedPriceImport(request),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.fixedPricesAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.priceListColumnNames(),
        }),
        refreshOffers(queryClient),
      ]),
  });
};

/** The column names of the price list upload (EOS-59), shared by everyone. */
export const usePriceListColumnNames = () =>
  useQuery<PriceListColumnNamesResponse>({
    queryKey: queryKeys.preisTool.priceListColumnNames(),
    queryFn: () => preisToolApi.priceListColumnNames(),
    staleTime: 60 * 1000,
  });

export const useSavePriceListColumnNames = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PriceListColumnNamesResponse,
    AxiosError,
    SavePriceListColumnNamesRequest
  >({
    mutationFn: (body) => preisToolApi.savePriceListColumnNames(body),
    onSuccess: (saved) =>
      queryClient.setQueryData(
        queryKeys.preisTool.priceListColumnNames(),
        saved,
      ),
  });
};

export const usePreisToolPriceWritebackStatus = () =>
  useQuery<PreisToolPriceWritebackStatusResponse>({
    queryKey: queryKeys.preisTool.priceWritebackStatus(),
    queryFn: () => preisToolApi.priceWritebackStatus(),
    staleTime: 5 * 60 * 1000,
  });

/** Saves one batch of prices; the list and the change logs are refetched. */
export const useSavePreisToolPrices = () => {
  const queryClient = useQueryClient();
  return useMutation<
    SavePreisToolPricesResponse,
    AxiosError,
    SavePreisToolPricesRequest
  >({
    mutationFn: (body) => preisToolApi.savePrices(body),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.itemsAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.preisTool.priceHistoryAll(),
        }),
      ]),
  });
};
